import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { AlertCircle, Check, ChevronDown, type LucideIcon } from "lucide-react";
import { cn } from "../lib/cn";

/* -------------------------------------------------------------------------- */
/* Field wrapper                                                               */
/* -------------------------------------------------------------------------- */

export interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  required?: boolean;
  htmlFor?: string;
  className?: string;
  children: ReactNode;
}

export function Field({
  label,
  hint,
  error,
  required,
  htmlFor,
  className,
  children,
}: FieldProps) {
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      {label && (
        <label
          htmlFor={htmlFor}
          className="text-sm font-medium text-content-muted"
        >
          {label}
          {required && (
            <span className="ml-0.5 text-crimson-600" aria-hidden="true">
              *
            </span>
          )}
        </label>
      )}
      {children}
      {error ? (
        <p className="flex items-center gap-1.5 text-xs text-danger-600 dark:text-danger-500">
          <AlertCircle size={13} aria-hidden="true" />
          {error}
        </p>
      ) : (
        hint && <p className="text-xs text-content-subtle">{hint}</p>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Input                                                                       */
/* -------------------------------------------------------------------------- */

const controlBase =
  "w-full rounded-lg border bg-surface text-content placeholder:text-content-subtle " +
  "transition-colors duration-200 " +
  "focus:outline-none focus:ring-2 focus:ring-gold-500/40 focus:border-gold-500 " +
  "disabled:cursor-not-allowed disabled:opacity-60";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  icon?: LucideIcon;
  trailing?: ReactNode;
  invalid?: boolean;
  inputSize?: "sm" | "md" | "lg";
}

const inputSizes = {
  sm: "h-9 text-sm",
  md: "h-10 text-sm",
  lg: "h-12 text-base",
} as const;

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { icon: Icon, trailing, invalid, inputSize = "md", className, ...props },
  ref,
) {
  return (
    <div className="relative flex items-center">
      {Icon && (
        <Icon
          size={16}
          aria-hidden="true"
          className="pointer-events-none absolute left-3 text-content-subtle"
        />
      )}
      <input
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cn(
          controlBase,
          inputSizes[inputSize],
          Icon ? "pl-9" : "pl-3",
          trailing ? "pr-10" : "pr-3",
          invalid
            ? "border-danger-500 focus:border-danger-500 focus:ring-danger-500/30"
            : "border-line-strong",
          className,
        )}
        {...props}
      />
      {trailing && (
        <span className="absolute right-2 flex items-center">{trailing}</span>
      )}
    </div>
  );
});

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }
>(function Textarea({ invalid, className, rows = 4, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      aria-invalid={invalid || undefined}
      className={cn(
        controlBase,
        "resize-y px-3 py-2 text-sm leading-relaxed",
        invalid ? "border-danger-500" : "border-line-strong",
        className,
      )}
      {...props}
    />
  );
});

/* -------------------------------------------------------------------------- */
/* Select                                                                      */
/* -------------------------------------------------------------------------- */

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "size"> {
  options: SelectOption[];
  icon?: LucideIcon;
  selectSize?: "sm" | "md" | "lg";
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { options, icon: Icon, selectSize = "md", className, ...props },
  ref,
) {
  return (
    <div className="relative flex items-center">
      {Icon && (
        <Icon
          size={16}
          aria-hidden="true"
          className="pointer-events-none absolute left-3 text-content-subtle"
        />
      )}
      <select
        ref={ref}
        className={cn(
          controlBase,
          inputSizes[selectSize],
          "cursor-pointer appearance-none pr-9",
          Icon ? "pl-9" : "pl-3",
          "border-line-strong",
          className,
        )}
        {...props}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
      <ChevronDown
        size={16}
        aria-hidden="true"
        className="pointer-events-none absolute right-3 text-content-subtle"
      />
    </div>
  );
});

/* -------------------------------------------------------------------------- */
/* Checkbox                                                                    */
/* -------------------------------------------------------------------------- */

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  label: ReactNode;
  description?: ReactNode;
  /** Gold in admin, crimson on the public cover filters. */
  tone?: "gold" | "crimson";
}

export function Checkbox({
  label,
  description,
  tone = "gold",
  className,
  id,
  checked,
  ...props
}: CheckboxProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  return (
    <label
      htmlFor={inputId}
      className={cn(
        "group flex cursor-pointer items-start gap-3 rounded-lg py-1.5 transition-colors",
        className,
      )}
    >
      <span className="relative mt-0.5 grid size-[18px] shrink-0 place-items-center">
        <input
          id={inputId}
          type="checkbox"
          checked={checked}
          className="peer sr-only"
          {...props}
        />
        <span
          aria-hidden="true"
          className={cn(
            "grid size-[18px] place-items-center rounded-[5px] border-2 border-line-strong bg-surface",
            "transition-all duration-200 ease-out",
            "group-hover:border-gold-400",
            "peer-focus-visible:ring-2 peer-focus-visible:ring-gold-500/40 peer-focus-visible:ring-offset-1",
            checked &&
              (tone === "gold"
                ? "border-gold-500 bg-gold-500"
                : "border-crimson-600 bg-crimson-600"),
          )}
        >
          <Check
            size={13}
            strokeWidth={3.5}
            className={cn(
              "text-white transition-all duration-150",
              tone === "gold" && "text-ink-950",
              checked ? "scale-100 opacity-100" : "scale-50 opacity-0",
            )}
          />
        </span>
      </span>
      <span className="min-w-0">
        <span className="block text-sm text-content">{label}</span>
        {description && (
          <span className="block text-xs text-content-subtle">{description}</span>
        )}
      </span>
    </label>
  );
}

/* -------------------------------------------------------------------------- */
/* Switch                                                                      */
/* -------------------------------------------------------------------------- */

export interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  /** Hide the visible label but keep it for screen readers. */
  hideLabel?: boolean;
  disabled?: boolean;
  size?: "sm" | "md";
  className?: string;
}

export function Switch({
  checked,
  onChange,
  label,
  hideLabel = false,
  disabled = false,
  size = "md",
  className,
}: SwitchProps) {
  const track = size === "sm" ? "h-5 w-9" : "h-6 w-11";
  const knob = size === "sm" ? "size-3.5" : "size-4.5";
  const travel = size === "sm" ? "translate-x-4" : "translate-x-5";

  return (
    <label className={cn("inline-flex items-center gap-2.5", className)}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={hideLabel ? label : undefined}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          "relative shrink-0 rounded-full border border-transparent transition-colors duration-250 ease-out",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500",
          "disabled:cursor-not-allowed disabled:opacity-50",
          track,
          checked ? "bg-gold-500" : "bg-line-strong",
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            "absolute top-1/2 left-0.5 -translate-y-1/2 rounded-full bg-white shadow-sm",
            "transition-transform duration-250 ease-[cubic-bezier(0.34,1.56,0.64,1)]",
            knob,
            checked && travel,
          )}
        />
      </button>
      {!hideLabel && (
        <span className="text-sm text-content-muted select-none">{label}</span>
      )}
    </label>
  );
}
